import {FC} from 'react'
import {useTranslation, TKey} from '@/translations/i18n.tsx'
import {useUnscheduleAssignment} from '@features/assignment/queries/useUnscheduleAssignment'
import {
    ModalBody,
    ModalCloseButton,
    ModalFooter,
    ModalHeader,
    ModalParagraph,
    ModalTitle
} from '@components/ui/modal-atoms'
import {Button} from '@components/ui/button'
import {Spinner} from '@components/ui/spinner'
import {AlertTriangleIcon} from '@components/ui/icon'
import {getServiceTypeDescriptionsVersion} from '@features/assignment/utils.tsx'
import {useAssignment} from '@features/assignment/queries/useAssignment'
import {useURLParsedParams} from '@hooks/useURLParsedParams.ts'
import {Assignment, AssignmentUrlParams} from '@features/assignment/types.ts'
import {ErrorBox} from '@components/commons/error-box'
import {Dialog, DialogClose, DialogContent} from '@components/ui/dialog'

const AssignmentUnscheduleModalContent = ({assignment, onClose}: {assignment: Assignment; onClose: () => void}) => {
    const {t} = useTranslation()
    const unscheduleAssignmentMutation = useUnscheduleAssignment({assignmentId: assignment.id})
    const serviceType = getServiceTypeDescriptionsVersion(assignment.task_media.name, assignment.task_category.name)

    return (
        <>
            <ModalHeader>
                <DialogClose
                    data-slot="dialog-close"
                    render={<ModalCloseButton disabled={unscheduleAssignmentMutation.isPending} />}
                />
            </ModalHeader>
            <ModalBody>
                <div className="flex flex-col gap-2">
                    <ModalTitle>{t(`assignment:unschedule_modal:title:${serviceType}` as TKey)}</ModalTitle>
                    <ModalParagraph>{t(`assignment:unschedule_modal:subtitle:${serviceType}` as TKey)}</ModalParagraph>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button onClick={onClose} variant="tertiary" disabled={unscheduleAssignmentMutation.isPending}>
                    {t('commons:close')}
                </Button>
                <Button
                    disabled={unscheduleAssignmentMutation.isPending}
                    onClick={() => unscheduleAssignmentMutation.mutate()}
                >
                    {t('assignment:unschedule')}
                    {unscheduleAssignmentMutation.isPending && <Spinner size={20} />}
                </Button>
            </ModalFooter>
        </>
    )
}

export const AssignmentUnscheduleModal: FC<{onClose: () => void}> = ({onClose}) => {
    const {t} = useTranslation()
    const urlParams = useURLParsedParams(AssignmentUrlParams)
    const assignmentQuery = useAssignment(urlParams.success ? urlParams.data.code : undefined)

    if (!assignmentQuery.isSuccess) {
        return <ErrorBox icon={<AlertTriangleIcon />} title={t('errors:default')} />
    }

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent
                className="w-145 grid grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden"
                showCloseButton={false}
            >
                <AssignmentUnscheduleModalContent assignment={assignmentQuery.data} onClose={onClose} />
            </DialogContent>
        </Dialog>
    )
}
